import React from 'react';
import { 
  GraduationCap, School, Hospital, TreePine, 
  ShoppingBag, Bus, Droplet, Lightbulb, 
  Eclipse, Wifi, Utensils, PawPrint, Dumbbell,
  Sofa, Shield, Check, MapPin, Home
} from 'lucide-react';

// Configuración de Pool Icon
const Pool = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M2 12h20M2 7h20M2 17h20M6 7v10M12 7v10M18 7v10" />
  </svg>
);

// Configuración de lugares cercanos con colores
const lugaresConfig = [
  { id: 'universidades', Icon: GraduationCap, label: 'Universidades', color: 'purple' },
  { id: 'colegios', Icon: School, label: 'Colegios', color: 'blue' },
  { id: 'hospitales', Icon: Hospital, label: 'Hospitales', color: 'red' },
  { id: 'parques', Icon: TreePine, label: 'Parques', color: 'green' },
  { id: 'centros_comerciales', Icon: ShoppingBag, label: 'Centros Comerciales', color: 'orange' },
  { id: 'transporte_publico', Icon: Bus, label: 'Transporte Público', color: 'yellow' }
];

// Configuración de servicios según operación con colores
const serviciosConfig = {
  venta: [
    { id: 'agua', Icon: Droplet, label: 'Agua', color: 'blue' },
    { id: 'luz', Icon: Lightbulb, label: 'Luz', color: 'yellow' },
    { id: 'desague', Icon: Eclipse, label: 'Desagüe', color: 'gray' },
    { id: 'internet', Icon: Wifi, label: 'Internet', color: 'green' },
    { id: 'seguridad', Icon: Shield, label: 'Seguridad', color: 'red' }
  ],
  alquiler: [
    { id: 'cocina_equipada', Icon: Utensils, label: 'Cocina Equipada', color: 'orange' },
    { id: 'piscina', Icon: Pool, label: 'Piscina', color: 'blue' },
    { id: 'pet_friendly', Icon: PawPrint, label: 'Pet Friendly', color: 'purple' },
    { id: 'gimnasio', Icon: Dumbbell, label: 'Gimnasio', color: 'red' },
    { id: 'seguridad', Icon: Shield, label: 'Seguridad', color: 'gray' }
  ]
};

const LugPublish = ({ 
  formData, 
  setFormData 
}) => {
  // Verificar si es un tipo de inmueble que puede estar amoblado
  const puedeEstarAmoblado = ['casa', 'departamento', 'local'].includes(formData.tipo);

  // Configuración de colores
  const getColorConfig = (color) => {
    const configs = {
      purple: { 
        bg: 'bg-purple-50', 
        text: 'text-purple-600', 
        border: 'border-purple-500', 
        hover: 'hover:border-purple-300',
        icon: 'text-purple-500',
        active: 'bg-purple-100'
      },
      blue: { 
        bg: 'bg-blue-50', 
        text: 'text-blue-600', 
        border: 'border-blue-500', 
        hover: 'hover:border-blue-300',
        icon: 'text-blue-500',
        active: 'bg-blue-100'
      },
      red: { 
        bg: 'bg-red-50', 
        text: 'text-red-600', 
        border: 'border-red-500', 
        hover: 'hover:border-red-300',
        icon: 'text-red-500',
        active: 'bg-red-100'
      },
      green: { 
        bg: 'bg-emerald-50', 
        text: 'text-emerald-600', 
        border: 'border-emerald-500', 
        hover: 'hover:border-emerald-300',
        icon: 'text-emerald-500',
        active: 'bg-emerald-100'
      },
      orange: { 
        bg: 'bg-orange-50', 
        text: 'text-orange-600', 
        border: 'border-orange-500', 
        hover: 'hover:border-orange-300',
        icon: 'text-orange-500',
        active: 'bg-orange-100'
      },
      yellow: { 
        bg: 'bg-yellow-50', 
        text: 'text-yellow-600', 
        border: 'border-yellow-500', 
        hover: 'hover:border-yellow-300',
        icon: 'text-yellow-500',
        active: 'bg-yellow-100'
      },
      gray: { 
        bg: 'bg-gray-50', 
        text: 'text-gray-600', 
        border: 'border-gray-500', 
        hover: 'hover:border-gray-300',
        icon: 'text-gray-500',
        active: 'bg-gray-100'
      },
      amber: { 
        bg: 'bg-amber-50', 
        text: 'text-amber-600', 
        border: 'border-amber-500', 
        hover: 'hover:border-amber-300',
        icon: 'text-amber-500',
        active: 'bg-amber-100'
      }
    };
    return configs[color] || configs.blue;
  };

  // Componente de botón de selección
  const SelectionButton = ({ 
    id, 
    Icon, 
    label, 
    isSelected, 
    onClick, 
    color = 'blue',
    description = null 
  }) => {
    const colors = getColorConfig(color);
    
    return (
      <button
        type="button"
        onClick={onClick}
        className={`group relative p-5 rounded-2xl border-2 transition-all duration-300 
                  transform hover:scale-105 hover:shadow-lg
                  ${isSelected
                    ? `${colors.border} ${colors.bg} shadow-lg scale-105`
                    : `border-gray-200 ${colors.hover} hover:bg-gray-50`
                  }`}
      >
        {/* Efecto de selección */}
        {isSelected && (
          <div className="absolute -top-2 -right-2 z-10">
            <div className={`p-1 rounded-full ${colors.bg} ${colors.border} border-2 shadow-lg`}>
              <Check className={`w-4 h-4 ${colors.text}`} />
            </div>
          </div>
        )}
        
        <div className="flex flex-col items-center gap-3">
          <div className={`p-3 rounded-xl transition-all duration-300 
                        ${isSelected ? colors.active : 'bg-gray-100 group-hover:bg-gray-200'}`}>
            <Icon className={`w-6 h-6 transition-all duration-300 
                           ${isSelected ? colors.icon : 'text-gray-500 group-hover:text-gray-600'}`} />
          </div>
          
          <div className="text-center">
            <span className={`text-sm font-semibold transition-all duration-300 
                           ${isSelected ? colors.text : 'text-gray-700 group-hover:text-gray-800'}`}>
              {label}
            </span>
            {description && (
              <p className="text-xs text-gray-500 mt-1 leading-tight">
                {description}
              </p>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header elegante */}
      <div className="text-center mb-8">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-orange-500 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-red-500 rounded-full"></div>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 
                         bg-clip-text text-transparent mb-3">
            Ubicación y Servicios
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></div>
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
          Define los lugares cercanos y {formData.operacion === 'alquiler' ? 'servicios incluidos' : 'servicios básicos'}
        </p>
      </div>

      {/* Sección de lugares cercanos */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100 rounded-t-2xl">
          <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
            <MapPin className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Lugares Cercanos</h3>
            <p className="text-gray-500 text-sm">Selecciona los lugares importantes cerca de la propiedad</p>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {lugaresConfig.map(({ id, Icon, label, color }) => (
              <SelectionButton
                key={id}
                id={id}
                Icon={Icon}
                label={label}
                color={color}
                isSelected={formData.lugares_cercanos.includes(id)}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    lugares_cercanos: prev.lugares_cercanos.includes(id)
                      ? prev.lugares_cercanos.filter(l => l !== id)
                      : [...prev.lugares_cercanos, id]
                  }));
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Sección de servicios */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-gray-100 rounded-t-2xl">
          <div className="p-2 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl">
            <Shield className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              {formData.operacion === 'alquiler' ? 'Servicios Incluidos' : 'Servicios Básicos'}
            </h3>
            <p className="text-gray-500 text-sm">
              {formData.operacion === 'alquiler' 
                ? 'Servicios y comodidades incluidas en el alquiler' 
                : 'Servicios básicos disponibles en la propiedad'}
            </p>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(formData.operacion === 'alquiler' ? serviciosConfig.alquiler : serviciosConfig.venta).map(({ id, Icon, label, color }) => (
              <SelectionButton
                key={id}
                id={id}
                Icon={Icon}
                label={label}
                color={color}
                isSelected={formData.servicios_basicos.includes(id)}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    servicios_basicos: prev.servicios_basicos.includes(id)
                      ? prev.servicios_basicos.filter(s => s !== id)
                      : [...prev.servicios_basicos, id]
                  }));
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Sección de amoblado */}
      {puedeEstarAmoblado && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-100 rounded-t-2xl">
            <div className="p-2 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl">
              <Home className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Estado del Mobiliario</h3>
              <p className="text-gray-500 text-sm">¿La propiedad viene amoblada o sin amueblar?</p>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, amoblado: "Si" }))}
                className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 
                          transform hover:scale-105 hover:shadow-lg
                          ${formData.amoblado === "Si"
                            ? 'border-amber-500 bg-amber-50 shadow-lg scale-105'
                            : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                          }`}
              >
                {/* Efecto de selección */}
                {formData.amoblado === "Si" && (
                  <div className="absolute -top-2 -right-2 z-10">
                    <div className="p-1 rounded-full bg-amber-50 border-2 border-amber-500 shadow-lg">
                      <Check className="w-4 h-4 text-amber-600" />
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center gap-4">
                  <div className={`p-4 rounded-xl transition-all duration-300 
                                ${formData.amoblado === "Si" ? 'bg-amber-100' : 'bg-gray-100 group-hover:bg-amber-100'}`}>
                    <Sofa className={`w-8 h-8 transition-all duration-300 
                                   ${formData.amoblado === "Si" ? 'text-amber-500' : 'text-gray-500 group-hover:text-amber-500'}`} />
                  </div>
                  
                  <div className="text-center">
                    <span className={`text-lg font-bold transition-all duration-300 
                                   ${formData.amoblado === "Si" ? 'text-amber-600' : 'text-gray-700 group-hover:text-amber-600'}`}>
                      Amoblado
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Incluye muebles y equipamiento básico
                    </p>
                  </div>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, amoblado: "No" }))}
                className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 
                          transform hover:scale-105 hover:shadow-lg
                          ${formData.amoblado === "No"
                            ? 'border-amber-500 bg-amber-50 shadow-lg scale-105'
                            : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                          }`}
              >
                {/* Efecto de selección */}
                {formData.amoblado === "No" && (
                  <div className="absolute -top-2 -right-2 z-10">
                    <div className="p-1 rounded-full bg-amber-50 border-2 border-amber-500 shadow-lg">
                      <Check className="w-4 h-4 text-amber-600" />
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center gap-4">
                  <div className={`p-4 rounded-xl transition-all duration-300 relative
                                ${formData.amoblado === "No" ? 'bg-amber-100' : 'bg-gray-100 group-hover:bg-amber-100'}`}>
                    <Sofa className={`w-8 h-8 transition-all duration-300 
                                   ${formData.amoblado === "No" ? 'text-amber-500' : 'text-gray-500 group-hover:text-amber-500'}`} />
                    <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300
                                   ${formData.amoblado === "No" ? 'text-amber-500' : 'text-gray-500 group-hover:text-amber-500'}`}>
                      <div className="w-px h-10 bg-current transform rotate-45"></div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <span className={`text-lg font-bold transition-all duration-300 
                                   ${formData.amoblado === "No" ? 'text-amber-600' : 'text-gray-700 group-hover:text-amber-600'}`}>
                      Sin Amueblar
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Espacio vacío listo para amueblar
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LugPublish;